<template>
  <div>
    <v-skeleton-loader
      v-if="isInitializing"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else
      :isSearchable="false"
      :headers="tableHeaders"
      :items="displayChangeEvents"
      :pagination="pagination"
      :loading="loading"
      :title="$t('sites.logs.title')"
      :sortableColumns="sortableColumns"
      hideDefaultFooter
      @sendQueryParams="fetchQuery"
    >
      <template #SearchWithFilters>
        <SearchWithFilters
          :searchPlaceholder="$t('sites.logs.searchBy')"
          :filters="filters"
          :filterName="filterName"
          :saveFilter="true"
          @onApplyFilters="handleApplyFilters"
          @onSearch="handleSearch"
        />
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
    <LoadingWidget
      :show="showLoadingWidget"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import LoadingWidget from '@/components/shared/widgets/LoadingWidget';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import moment from 'moment';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import filterMixin from '@/mixins/filterMixin';

export default {
    components: {
        LoadingWidget,
        TableRemote,
        SearchWithFilters,
    },
    mixins: [
        filterMixin,
    ],
    data() {
        return {
            loading: false,
            isInitializing: true,
            showLoadingWidget: false,
            siteChangeEvents: [],
            pagination: {},
            filterName: 'logsFilter',
            queryParams: {
                perpage: 25,
                page: 1,
                startDate: moment().subtract(14, 'days').toISOString(),
                endDate: moment().endOf('day').toISOString(),
            },
            filters: [
                {
                    key: 'date',
                    label: 'laboratory.labels.date',
                    type: 'date',
                    value: [moment().subtract(14, 'days').toISOString(), moment().toISOString()],
                    dateKeys: ['startDate', 'endDate'],
                    isRange: true,
                    datacy: 'LabReportDate',
                },
            ],
        };
    },
    computed: {
        ...mapGetters([
            'lang',
            'hasRole',
            'hasPermission',
        ]),
        tableHeaders() {
            const headers = [
                'date',
                'createdBy',
                'httpMethod',
                'details',
            ].map(value => ({
                sortable: false,
                text: this.$t(`sites.logs.labels.${value}`),
                value,
            }));
            return headers;
        },
        displayChangeEvents() {
            return this.siteChangeEvents.map(event => ({
                httpMethod: event.httpMethod,
                details: event.details,
                date: this.$options.filters.formatDate(moment(event.date), 'L LT'),
                createdBy: event.createdBy,
            }));
        },
    },

    watch: {
        async lang() {
            await this.getSiteChangeEvents();
        },
    },

    async created() {
        this.getFilterValues();
        await this.getSiteChangeEvents();
        this.isInitializing = false;
    },

    methods: {
        async getSiteChangeEvents() {
            if (this.loading) return;
            this.loading = true;
            const { siteId } = this.$route.params;
            const { clientId } = this.$route.params;
            const { data } = await await this.$rq.getSiteChangeEvents(siteId, clientId, this.queryParams);
            this.siteChangeEvents = [...this.siteChangeEvents, ...data.siteChangeEvents];
            this.pagination = data.pagination;
            this.loading = false;
        },

        async fetchQuery(queryParams) {
            this.queryParams = { ...this.queryParams, ...queryParams };
            await this.getSiteChangeEvents();
        },

        handleSearch(search) {
            this.queryParams = {
                ...this.queryParams,
                search,
            };
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            this.queryParams = cleanQueryParams;
            this.pagination = {};
            this.siteChangeEvents = [];
            this.getSiteChangeEvents();
        },
        handleApplyFilters(filterParams) {
            this.queryParams = {
                ...this.queryParams,
                ...filterParams,
            };
            if (filterParams?.reset) {
                this.getFilterValues(true);
            }
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            this.queryParams = cleanQueryParams;
            this.pagination = {};
            this.siteChangeEvents = [];
            this.getSiteChangeEvents();
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition && !this.isFetching) {
                this.queryParams.page += 1;
                await this.getSiteChangeEvents();
                // access "v-autocomplete" ref
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
</style>;
